import { forwardRef, memo, HTMLProps } from 'react'
import { useSelector } from 'react-redux'
import { H1, Typography, Divider, SkeletonTextLine, SkeletonText } from '@vp/swan'

import { getH1Title, booleanRenderPropertySelector } from '~/app/store/config'
import { RenderProperty } from 'shared/renderProperties'
import { getMetadata } from '~/app/store/config/reducer'
import { BreadcrumbsContainer } from '~/app/features/Gallery/components/Header/HeroHeader/components/BreadcrumbsContainer'
import { RatingsSummary } from '~/app/features/Gallery/components/Header/HeroHeader/components/RatingsSummary'
import { useRatingsAndReviews } from '~/app/features/Gallery/hooks/useRatingsAndReviews'
import { SetHighlightingHeader } from '~/app/features/Gallery/components/SetHighlighting/SetHighlightingHeader'
import { getShowSets } from '~/app/store/debug'

export const HeroHeader = memo(
  forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement>>((props, ref) => {
    const h1Title = useSelector(getH1Title)
    const booleanRenderProperty = useSelector(booleanRenderPropertySelector)
    const galleryMetadata = useSelector(getMetadata)
    const showSets = useSelector(getShowSets)
    const hasValidBreadcrumbs = galleryMetadata?.hasValidBreadcrumbs

    const showBreadcrumbs = booleanRenderProperty(RenderProperty.ShowBreadcrumbs)
    const showProductHeaderInfo = booleanRenderProperty(RenderProperty.ShowProductHeaderInfo)

    const { data: ratingsAndReviews, isLoading } = useRatingsAndReviews()

    return (
      <section className="gallery-hero-header" {...props} ref={ref}>
        <div className="hero-container">
          {showBreadcrumbs && hasValidBreadcrumbs && <BreadcrumbsContainer />}
          <div className="title-primary">
            {!!h1Title && (
              <H1 fontSkin="title-headline" data-testid="page-title">
                {h1Title}
              </H1>
            )}
            {showProductHeaderInfo && !!galleryMetadata && !!galleryMetadata?.primaryContent && (
              <div className="main-content-container">
                <Typography className="main-content">{galleryMetadata?.primaryContent}</Typography>
              </div>
            )}
          </div>
          {showSets && <SetHighlightingHeader />}
          {isLoading && (
            <SkeletonText className="ratings-summary-skeleton" data-testid="ratings-spinner">
              <SkeletonTextLine></SkeletonTextLine>
            </SkeletonText>
          )}
          {ratingsAndReviews && <RatingsSummary {...ratingsAndReviews} />}
          <Divider mt="6" />
        </div>
      </section>
    )
  }),
)

HeroHeader.displayName = 'HeroHeader'
